import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router, RouterEvent } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import Changeover from 'src/models/changeover';
import Task from 'src/models/task';
import { ApiService } from '../services/api.service';
import { LiveUpdateService } from '../services/live-update.service';
import { UserSessionService } from '../services/user-session.service';
import { TaskBottomSheetComponent } from './task-bottom-sheet/task-bottom-sheet.component';
import { TaskListComponent } from './task-list/task-list.component';
import * as moment from 'moment';

@Component({
  selector: 'app-changeover',
  templateUrl: './changeover.component.html',
  styleUrls: ['./changeover.component.scss']
})
export class ChangeoverComponent implements OnInit, OnDestroy {
  currentChangeover: Changeover | null = null;
  nextChangover: Changeover | null = null;
  get nextScheduledTime() { return this.nextChangover && moment(this.nextChangover.scheduledTime).format('LLLL') };
  modelFetchPromise: Promise<Changeover | null> | null = null;

  selectedTaskId: string | null = null;
  subscriptions = new Array<Subscription>();

  @ViewChild('taskList')
  taskList!: TaskListComponent;

  constructor(
    private api: ApiService,
    private userSessionService: UserSessionService,
    private liveUpdateService: LiveUpdateService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly router: Router,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.liveUpdateService.TaskEventUpdated.subscribe(item => this.updateTask(item.taskId)),
      this.liveUpdateService.ChangeoverFinalised.subscribe(item => this.loadData()),
      this.liveUpdateService.CurrentChangeoverChanged.subscribe(item => this.loadData()),
      this.router.events.pipe(filter((e): e is RouterEvent => e instanceof RouterEvent)).subscribe(e => this.onTaskSelected(null)),
      this.userSessionService.activeLineIdChanged.subscribe(lineId => this.loadData()),
    );
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadData = async () => {
    this.currentChangeover = null;
    this.nextChangover = null;

    let isErrored = false;
    try {
      this.modelFetchPromise = this.api.getCurrentChangeover(this.userSessionService.activeLineId);
      this.currentChangeover = await this.modelFetchPromise;
      this.modelFetchPromise = null;
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      if (httpError.status === 404) {
        this.modelFetchPromise = null;
      } else {
        isErrored = true;
      }
    }

    if (!isErrored && (this.currentChangeover === null || this.currentChangeover.isFinalised)) {
      try {
        this.modelFetchPromise = this.api.getNextChangeover(this.userSessionService.activeLineId);
        this.nextChangover = await this.modelFetchPromise;
        this.modelFetchPromise = null;
      } catch (error) {
        const httpError = error as HttpErrorResponse;
        if (httpError.status === 404) {
          this.modelFetchPromise = null;
        }
      }
    }
  }

  updateTask = async (taskId: string) => {
    if (this.currentChangeover && this.currentChangeover.tasks.some(item => item.id === taskId)) {
      const updatedTask = await this.api.getChangeoverTask(this.currentChangeover.id, taskId);
      const oldTask = this.currentChangeover.tasks.find(item => item.id == taskId)!;
      // We apply the updated data to the old task instance to avoid interfering with mat-selection-list item focus
      Object.assign(oldTask, updatedTask);
      this.taskList?.refreshTask(taskId);

      if (this.selectedTaskId === taskId) {
        this.onTaskSelected(oldTask);
      }
    }
  }

  onTaskSelected(selection: Task | null): void {
    this.selectedTaskId = selection?.id || null;
    if (selection) {
      if (!this.bottomSheet._openedBottomSheetRef) {
        this.bottomSheet.open<TaskBottomSheetComponent, Task>(
          TaskBottomSheetComponent,
          {
            data: selection,
            hasBackdrop: false,
            panelClass: 'changeover-bottom-sheet'
          }
        );
      } else {
        const sheet = this.bottomSheet._openedBottomSheetRef.instance as TaskBottomSheetComponent;
        sheet.update(selection);
      }
    }

    if (this.bottomSheet._openedBottomSheetRef && !selection) {
      this.bottomSheet.dismiss();
    }
  }

  startNext = async () => {
    if (this.nextChangover) {
      await this.api.setCurrentChangeover(this.userSessionService.activeLineId, this.nextChangover.id);
    }
  }

  get noCurrentChangeover() {
    return !this.modelFetchPromise && this.currentChangeover === null;
  }

  get nothingScheduled() {
    return !this.modelFetchPromise && this.nextChangover === null && this.currentChangeover === null;
  }
}
